import { IonIcon, IonInput, IonItem } from "@ionic/react";
import { searchOutline, searchSharp } from "ionicons/icons";
import React, { useCallback, useRef, useState } from "react";
import FilterModal from "./FilterModal";
import { ApiResponse } from "../models/api/ApiResponse";
import { getValueSearchDetails } from "../api/QueryDesigner";
import ISSPopover from "./ISSPopover";

export type SearchData = {
  code: string;
  description: string;
};

type Props = {
  value?: string | number | null;
  tableName: string;
  columnName: string;
  /**User is typing inside input field to search */
  onSearch: (searchQuery: string) => Promise<SearchData[]>;
  /**User choose a value from the search result */
  onSelect: (value: string) => void;
  /**Input value change, when user types in the search input */
  onChange: (value: string) => void;
};

const ISSElement: React.FC<Props> = (props) => {
  const { value, columnName, tableName, onSearch, onSelect, onChange } = props;
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterModalModuleName, setFilterModalModuleName] = useState("");
  const [_, setFilterModalColumnName] = useState("");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [searchData, setSearchData] = useState<SearchData[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const popover = useRef<HTMLIonPopoverElement>(null);

  const handleSearch = useCallback(
    async (e: CustomEvent) => {
      const searchQuery = (e.target as HTMLInputElement)?.value;
      onChange(searchQuery);

      setSearchValue(searchQuery);
      if (searchQuery.length > 2) {
        const searchResult = await onSearch(searchQuery);
        setSearchData(searchResult);
        popover.current!.event = e;
        setPopoverOpen(searchResult.length > 0);
      } else {
        setPopoverOpen(false);
        setSearchData([]);
      }
    },
    [onChange, onSearch]
  );

  const closeFilterModal = useCallback(() => {
    setIsFilterModalOpen(false);
    setFilterModalModuleName("");
  }, []);

  const handleConfirm = useCallback(
    (value: string) => {
      onSelect(value);
      closeFilterModal();
    },
    [closeFilterModal, onSelect]
  );

  return (
    <>
      <IonItem class="wo-editable-container">
        <IonInput
          class="wo-editable-input-assigned"
          value={value}
          onIonInput={handleSearch}
        ></IonInput>
        <IonIcon
          class="search-icon"
          slot="end"
          ios={searchOutline}
          md={searchSharp}
          onClick={async () => {
            setFilterModalColumnName(columnName);
            const response: ApiResponse = await getValueSearchDetails({
              tableName,
              columnName,
            });
            if (response.status === 200) {
              setFilterModalModuleName(response.data.QueryWizardModuleName);
              setIsFilterModalOpen(true);
            }
          }}
        ></IonIcon>
      </IonItem>
      <FilterModal
        isOpen={isFilterModalOpen}
        moduleName={filterModalModuleName}
        cancel={closeFilterModal}
        confirm={handleConfirm}
      />
      <ISSPopover
        popover={popover}
        popoverOpen={popoverOpen}
        setPopoverOpen={setPopoverOpen}
        searchResults={searchData.map((s) => s.code)}
        setSearchResults={() => setSearchData([])}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        setElementValue={onSelect}
      />
    </>
  );
};

export default ISSElement;
